import { AuthenticationService } from '@/core/services/authentication/authentication.service';
import { MeProfile } from '@/core/services/profile/me-profile.model';
import { MessagesInboxComponent } from '@/features/messages/components/messages-inbox/messages-inbox.component';
import { LayoutService } from '@/layout/service/layout.service';
import { HomeState } from '@/modules/home/core/state/home.state';
import { ContrastService } from '@/shared/services/contrast.service';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  inject,
  model,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { MenuItem } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { TooltipModule } from 'primeng/tooltip';
import { filter } from 'rxjs';
import { MenuStateService } from '../service/menu-state.service';
import { NotificationsMenuComponent } from './notifications-menu/notifications-menu.component';

interface NotificationsBars {
  id: string;
  label: string;
  badge?: string | any;
}
@Component({
  selector: '[app-topbar]',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    FormsModule,
    StyleClassModule,
    InputTextModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    RippleModule,
    BadgeModule,
    OverlayBadgeModule,
    AvatarModule,
    TranslateModule,
    TooltipModule,
    NotificationsMenuComponent,
    MessagesInboxComponent,
    AutoCompleteModule,
  ],
  styles: [
    `
      :host {
        position: sticky;
        top: 0;
        z-index: 999;
        display: block;
      }

      .app-config-button::before,
      .app-config-button::after {
        display: none !important;
      }

      .topbar-right {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-left: auto;
      }

      .topbar-menu-search {
        width: 340px;
      }

      .menu-search-wrapper {
        position: relative;
        width: 100%;
      }

      :host ::ng-deep .menu-search-autocomplete {
        width: 100%;
        display: block;
      }

      :host ::ng-deep .menu-search-autocomplete input,
      :host ::ng-deep .menu-search-input {
        width: 100%;
        height: 38px;
        border-radius: 8px;
        padding-right: 2.75rem;
      }

      .menu-search-icon {
        position: absolute;
        right: 14px;
        top: 50%;
        transform: translateY(-50%);
        color: #7f8ea3;
        pointer-events: none;
        font-size: 0.95rem;
        z-index: 2;
      }

      :host ::ng-deep .menu-search-input {
        width: 100%;
        height: 38px;
        border-radius: 8px;
        padding-left: 1rem;
      }

      .menu-search-option {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.25rem 0;
      }

      .menu-search-option i {
        font-size: 0.9rem;
      }

      .profile-access-info {
        padding: 0.75rem;
      }

      .profile-user {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
        margin-bottom: 0.75rem;
      }

      .profile-user strong {
        font-size: 0.95rem;
        color: var(--text-color);
      }

      .profile-user span {
        font-size: 0.75rem;
        color: var(--text-color-secondary);
        text-transform: capitalize;
      }

      .profile-access-divider {
        height: 1px;
        background: var(--surface-border);
        margin: 0.75rem 0;
      }

      .profile-access-row {
        display: flex;
        align-items: center;
        gap: 0.625rem;
        padding: 0.5rem 0;
        color: var(--text-color);
      }

      .profile-access-row i {
        color: var(--primary-color);
        font-size: 0.95rem;
      }

      .profile-access-row div {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
      }

      .profile-access-row small {
        font-size: 0.7rem;
        color: var(--text-color-secondary);
      }

      .profile-access-row strong {
        font-size: 0.8rem;
        font-weight: 600;
        color: var(--text-color);
      }
    `,
  ],
  template: `
    @if (!hideTopbar) {
      <div class="layout-topbar">
        <div class="topbar-left">
          <img
            class="horizontal-logo"
            src="/layout/images/camerite-simbolo.svg"
            alt="logo"
          />

          <span class="topbar-separator"></span>

          <a routerLink="/">
            <img
              class="mobile-logo"
              src="/layout/images/camerite-simbolo.svg"
              alt="logo"
            />
          </a>
        </div>

        <div class="topbar-right">
          <div class="topbar-menu-search">
            <div class="menu-search-wrapper">
              <p-autocomplete
                [(ngModel)]="menuSearchValue"
                [suggestions]="filteredMenuItems"
                (completeMethod)="searchMenuItems($event)"
                (onSelect)="onMenuSearchSelect($event)"
                optionLabel="label"
                placeholder="Buscar no menu"
                styleClass="menu-search-autocomplete"
                inputStyleClass="menu-search-input"
              >
                <ng-template pTemplate="item" let-item>
                  <div class="menu-search-option">
                    <i [class]="item.icon"></i>
                    <span>{{ item.label }}</span>
                  </div>
                </ng-template>
              </p-autocomplete>

              <i class="pi pi-search menu-search-icon"></i>
            </div>
          </div>

          <ul class="topbar-menu">
            <li class="right-sidebar-item">
              <button class="app-config-button" (click)="onConfigButtonClick()">
                <i class="pi pi-cog"></i>
              </button>
            </li>

            <li class="right-sidebar-item static sm:relative">
              <cam-messages-inbox />
            </li>

            <cam-notifications-menu></cam-notifications-menu>

            <li class="profile-item static sm:relative">
              <a
                class="right-sidebar-button relative z-50"
                pStyleClass="@next"
                enterFromClass="hidden"
                enterActiveClass="animate-scalein"
                leaveActiveClass="animate-fadeout"
                leaveToClass="hidden"
                [hideOnOutsideClick]="true"
              >
                <p-avatar styleClass="!w-10 !h-10">
                  <img [src]="me().avatarUrl" alt="" />
                </p-avatar>
              </a>

              <div
                class="list-none p-2 m-0 rounded-2xl border border-surface overflow-hidden absolute bg-surface-0 dark:bg-surface-900 hidden origin-top w-72 mt-2 right-0 z-[999] top-auto shadow-[0px_56px_16px_0px_rgba(0,0,0,0.00),0px_36px_14px_0px_rgba(0,0,0,0.01),0px_20px_12px_0px_rgba(0,0,0,0.02),0px_9px_9px_0px_rgba(0,0,0,0.03),0px_2px_5px_0px_rgba(0,0,0,0.04)]"
              >
                <div class="profile-access-info">
                  <div class="profile-user">
                    <strong>{{ me().name }}</strong>
                    <span>{{ me().role }}</span>
                  </div>

                  <div class="profile-access-divider"></div>

                  <div class="profile-access-row">
                    <i class="pi pi-desktop"></i>
                    <div>
                      <small>Plataforma</small>
                      <strong>{{ me().whitelabel?.name }}</strong>
                    </div>
                  </div>

                  <div class="profile-access-row">
                    <i class="pi pi-clock"></i>
                    <div>
                      <small>Acesso</small>
                      <strong>{{ accessDateTime() }}</strong>
                    </div>
                  </div>
                </div>

                <ul class="flex flex-col gap-1">
                  <li>
                    <a
                      class="label-small flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer"
                      [ngStyle]="logoutColor()"
                      (click)="logout()"
                    >
                      <i class="pi pi-power-off"></i>
                      <span>{{ 'sair' | translate }}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </div>
      </div>
    }
  `,
})
export class AppTopbar {
  layoutService = inject(LayoutService);
  private readonly store = inject(Store);
  public readonly router = inject(Router);
  public readonly authService = inject(AuthenticationService);
  protected readonly contrastService = inject(ContrastService);
  private readonly menuStateService = inject(MenuStateService);

  protected menuSearchValue: any = null;
  protected filteredMenuItems: MenuItem[] = [];

  isDarkTheme = computed(() => this.layoutService.isDarkTheme());
  me = this.store.selectSignal<MeProfile>(HomeState.getMe);

  protected accessDateTime = computed(() => {
    return new Date().toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  });

  protected currentUrl = signal(this.router.url);

  logoutColor = computed(() => ({
    color: this.isDarkTheme() ? '#ffffff' : '#000000',
  }));

  @ViewChild('menubutton') menuButton!: ElementRef;

  notificationSearch = '';

  notificationsBars = signal<NotificationsBars[]>([
    {
      id: 'inbox',
      label: 'Inbox',
      badge: '2',
    },
    {
      id: 'general',
      label: 'General',
    },
    {
      id: 'archived',
      label: 'Archived',
    },
  ]);

  notifications = signal<any[]>([
    {
      id: 'inbox',
      data: [
        {
          image: '/demo/images/avatar/avatar-square-m-2.jpg',
          name: 'Michael Lee',
          description:
            'You have a new message from the support team regarding your recent inquiry.',
          time: '1 hour ago',
          attachment: {
            title: 'Contract',
            size: '2.1 MB',
          },
          read: false,
        },
        {
          image: '/demo/images/avatar/avatar-square-f-1.jpg',
          name: 'Alice Johnson',
          description:
            'Your report has been successfully submitted and is under review.',
          time: '10 minutes ago',
          read: true,
        },
        {
          image: '/demo/images/avatar/avatar-square-f-2.jpg',
          name: 'Emily Davis',
          description:
            'The project deadline has been updated to September 30th. Please check the details.',
          time: 'Yesterday at 4:35 PM',
          read: false,
        },
      ],
    },
    {
      id: 'general',
      data: [
        {
          image: '/demo/images/avatar/avatar-square-f-1.jpg',
          name: 'Alice Johnson',
          description:
            'Reminder: Your subscription is about to expire in 3 days. Renew now to avoid interruption.',
          time: '30 minutes ago',
          read: true,
        },
        {
          image: '/demo/images/avatar/avatar-square-m-2.jpg',
          name: 'Michael Lee',
          description:
            'The server maintenance has been completed successfully. No further downtime is expected.',
          time: 'Yesterday at 2:15 PM',
          read: false,
        },
      ],
    },
    {
      id: 'archived',
      data: [
        {
          image: '/demo/images/avatar/avatar-square-m-1.jpg',
          name: 'Lucas Brown',
          description:
            'Your appointment with Dr. Anderson has been confirmed for October 12th at 10:00 AM.',
          time: '1 week ago',
          read: false,
        },
        {
          image: '/demo/images/avatar/avatar-square-f-2.jpg',
          name: 'Emily Davis',
          description:
            'The document you uploaded has been successfully archived for future reference.',
          time: '2 weeks ago',
          read: true,
        },
      ],
    },
  ]);

  selectedNotificationBar = model(this.notificationsBars()[0].id ?? 'inbox');

  selectedNotificationsBarData = computed(
    () =>
      this.notifications().find((f) => f.id === this.selectedNotificationBar())
        .data,
  );

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.currentUrl.set(this.router.url);
      });
  }

  get hideTopbar(): boolean {
    return this.router.url.includes('mosaic-view');
  }

  private normalizeSearchText(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  protected searchMenuItems(event: { query: string }): void {
    const query = this.normalizeSearchText(event.query);

    const allItems = [
      ...this.flattenMenuItems(this.menuStateService.topbarItems()),
      ...this.flattenMenuItems(this.menuStateService.sidebarItems()),
      ...this.flattenMenuItems(this.menuStateService.configuratorItems()),
    ];

    if (!query) {
      this.filteredMenuItems = allItems;
      return;
    }

    this.filteredMenuItems = allItems.filter((item) => {
      const label = this.normalizeSearchText(item.label);

      return label.includes(query);
    });
  }

  private flattenMenuItems(items: MenuItem[]): MenuItem[] {
    return items.flatMap((item) => {
      const children = item.items?.length
        ? this.flattenMenuItems(item.items)
        : [];

      const currentItem =
        item.routerLink || item.command
          ? [
              {
                ...item,
                items: undefined,
              },
            ]
          : [];

      return [...currentItem, ...children];
    });
  }

  protected onMenuSearchSelect(event: any): void {
    const item = event.value as MenuItem;

    if (item.command) {
      item.command({
        originalEvent: event.originalEvent,
        item,
      });
      this.menuSearchValue = null;
      return;
    }

    if (item.routerLink) {
      this.router.navigate(
        Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink],
      );
      this.menuSearchValue = null;
    }
  }

  onMenuButtonClick() {
    this.layoutService.onMenuToggle();
  }

  showRightMenu() {
    this.layoutService.toggleRightMenu();
  }

  onConfigButtonClick() {
    this.layoutService.showConfigSidebar();
  }

  toggleSearchBar() {
    this.layoutService.layoutState.update((value) => ({
      ...value,
      searchBarActive: !value.searchBarActive,
    }));
  }

  logout() {
    this.authService.logout().subscribe(() => {
      this.router.navigate(['/login']);
    });
  }
}
